import { PopProvider } from '@/components/Pop';
import { ToastProvider } from '@/components/Toast';
import { tokenInfo, usAppLogin, user } from '@/services/store';
import { HashRouter, Route, useLocation } from '@solidjs/router';
import WebApp from '@twa-dev/sdk';
import { Component, createEffect, createSignal, JSX, lazy, onCleanup, onMount, Show, Suspense } from 'solid-js';
import TabBar from './components/tabbar';
import { Toggle } from './components/Toggle';
import { Transition } from './components/Transition';
import Welcome from './pages/Welcome/Welcome';
import { AUDIO_TRACK, AudioPlayer } from './services/audio';
import { globalState } from './stores/global';
import { setting } from './stores/setting';
import useCheckInviteLink from './hooks/useCheckInviteLink';

const Home = lazy(() => import('./pages/Home/Home'));
const LevelPreview = lazy(() => import('./pages/Home/LevelPreview'));
const Test = lazy(() => import('./pages/Test/Test'));
const Boost = lazy(() => import('./pages/Boost'));
const Friends = lazy(() => import('./pages/Friends/Friends'));
const FriendsRank = lazy(() => import('./pages/Friends/FriendsRank'));
const LeaderBoard = lazy(() => import('./pages/LeaderBoard'));
const Bonus = lazy(() => import('./pages/Bonus/Bonus'));
const EventChristmas = lazy(() => import('./pages/Event/Christmas/Index'));
const EventMyBag = lazy(() => import('./pages/Event/Christmas/MyBag'));
const EventShop = lazy(() => import('./pages/Event/Christmas/Shop'));
const EventRanking = lazy(() => import('./pages/Event/Christmas/Ranking'));
const GameIndex = lazy(() => import('./pages/Games/Index'));
const LuckyRaffle = lazy(() => import('./pages/Raffle/Index'));


const Setting = lazy(() => import('./pages/Setting/Setting'));
const SelectLanguage = lazy(() => import('./pages/Setting/SelectLanguage'));
const Airdrop = lazy(() => import('./pages/Airdrop'));
const HistoryClaims = lazy(() => import('./pages/Airdrop/HistoryClaims'));
const ClaimPage = lazy(() => import('./pages/Airdrop/ClaimPage'));
const MemoryRecap = lazy(() => import('./pages/Airdrop/MemoryRecap'));

function Root(props: { children: JSX.Element }) {
  const [bgmReady, setBgmReady] = createSignal(false);
  const [currentLevel, setCurrentLevel] = createSignal(user.level);
  const [isInitialized, setIsInitialized] = createSignal(false);
  const location = useLocation();


  const handleBGMEffect = function (e: Event) {
    if (bgmReady()) {
      document.getElementById('root')?.removeEventListener('click', handleBGMEffect);
      return;
    }
    if (setting.musicEnable) {
      AudioPlayer.play(AUDIO_TRACK.BGM, true);
    }
    setBgmReady(true);
  };

  const handleLevelUpEffect = function () {
    // 初始化的数据是 0，不管
    if (!user.level) return;

    // 如果尚未初始化，设置当前等级并标记为已初始化
    if (!isInitialized()) {
      setCurrentLevel(user.level);
      setIsInitialized(true);
      return;
    }

    if (currentLevel() !== user.level) {
      AudioPlayer.play(AUDIO_TRACK.LEVEL_UP);
      setCurrentLevel(user.level);
    }
  };

  createEffect(() => {
    if (location.pathname === '/') {
      WebApp.BackButton.hide();
    } else {
      WebApp.BackButton.show();
    }
  });

  // 设置开启的时候播放
  createEffect(() => {
    if (!setting.musicEnable) {
      AudioPlayer.stopLoop();
    } else {
      bgmReady() && AudioPlayer.play(AUDIO_TRACK.BGM, true);
    }
  }, [setting.musicEnable]);

  createEffect(() => {
    handleLevelUpEffect();
  }, [user.level]);

  onMount(() => {
    function setRootFontSize() {
      document.documentElement.style.fontSize = `${(document.documentElement.clientWidth / 430) * 16}px`;
    }
    setRootFontSize();
    window.addEventListener('resize', setRootFontSize, false);
    onCleanup(() => {
      window.removeEventListener('resize', setRootFontSize);
    });

    // 进入页面，有触发任何 click 事件播放（相当于自动播放）
    document.getElementById('root')?.addEventListener('click', handleBGMEffect);
    onCleanup(() => {
      document.getElementById('root')?.removeEventListener('click', handleBGMEffect);
    });
  });

  return (
    <>
      <ToastProvider />
      <PopProvider />
      <Suspense fallback={<Welcome />}>
        <Home class={location.pathname === '/' ? '' : 'hidden'} />
        {props.children}
      </Suspense>
      <Transition name="fadeUp">
        <Toggle when={globalState.showTabBar}>
          <TabBar />
        </Toggle>
      </Transition>
    </>
  );
}

const App = () => {
  usAppLogin();


  return (
    <Show when={tokenInfo.token} fallback={<Welcome />}>
      <HashRouter root={Root as Component}>
        {import.meta.env.DEV && <Route path="/test" component={Test} />}
        <Route path="/boost" component={SuspenseComponent(Boost)} />
        <Route path="/friends" component={SuspenseComponent(Friends)} />
        <Route path="/friends/rank" component={SuspenseComponent(FriendsRank)} />
        <Route path="/rank" component={SuspenseComponent(LeaderBoard)} />
        <Route path="/setting" component={SuspenseComponent(Setting)} />
        <Route path="/setting/language" component={SuspenseComponent(SelectLanguage)} />
        <Route path="/bonus" component={SuspenseComponent(Bonus)} />
        <Route path="/airdrop" component={SuspenseComponent(Airdrop)} />
        <Route path="/airdrop/history" component={SuspenseComponent(HistoryClaims)} />
        <Route path="/airdrop/claim" component={SuspenseComponent(ClaimPage)} />
        <Route path="/airdrop/memory/:id" component={SuspenseComponent(MemoryRecap)} />
        <Route path="/level-preview" component={SuspenseComponent(LevelPreview)} />
        <Route path="/event/christmas" component={SuspenseComponent(EventChristmas)} />
        <Route path="/event/christmas/:openid" component={SuspenseComponent(EventChristmas)} />
        <Route path="/event/christmas/mybag" component={SuspenseComponent(EventMyBag)} />
        <Route path="/event/christmas/shop" component={SuspenseComponent(EventShop)} />
        <Route path="/event/christmas/ranking" component={SuspenseComponent(EventRanking)} />
        <Route path="/event/raffle" component={SuspenseComponent(LuckyRaffle)} />
        <Route path="/games" component={SuspenseComponent(GameIndex)} />
      </HashRouter>
    </Show>
  );
};

export default App;

function SuspenseComponent(InnerComponent: Component) {
  return () => {
    return (
      <Suspense fallback={<Welcome />}>
        <InnerComponent />
      </Suspense>
    );
  };
}
